import { Alert, Button, Dialog, Portal, Text, Field, Box, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

const COPY = {
  title: 'Rechnung stornieren',
  body:
    'Mit der Stornierung wird automatisch eine Stornorechnung erstellt.\n' +
    'Der angegebene Stornogrund wird im Rechnungsvortext ausgewiesen.\n' +
    'Die Stornierung ist rechtlich bindend und kann nicht rückgängig gemacht werden.',
  reasonLabel: 'Stornogrund',
  reasonPlaceholder: 'Stornogrund eingeben…',
  reasonRequired: 'Bitte geben Sie einen Stornogrund ein.',
  counter: (n: number, max: number) => `${n} von ${max} Zeichen.`,
  cancel: 'Abbrechen',
  confirm: 'Rechnung stornieren',
}

interface CancelInvoiceDialogProps {
  isOpen: boolean
  onClose: () => void
  loading?: boolean
  error?: string | null
  onConfirm: (cancellationReason: string) => Promise<void> | void
}

const MAX_LEN = 3000

export function CancelInvoiceDialog({
  isOpen,
  onClose,
  onConfirm,
  loading,
  error,
}: CancelInvoiceDialogProps) {
  // const locale = getLocale()
  const t = COPY

  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setReason('')
    setTouched(false)
  }, [isOpen])

  const trimmed = reason.trim()
  const reasonError = touched && !trimmed ? t.reasonRequired : null
  const canSubmit = !loading && !!trimmed

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => {
        if (!details.open) onClose()
      }}
      size="md"
      closeOnInteractOutside={false}
      closeOnEscape={false}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{t.title}</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body whiteSpace="pre-line">
              {t.body}

              <Box mt="4">
                <Field.Root invalid={!!reasonError}>
                  <Field.Label>
                    {t.reasonLabel}
                    <Text as="span" color="fg.error">
                      {' '}
                      *
                    </Text>
                  </Field.Label>

                  <Textarea
                    value={reason}
                    onChange={e => setReason(e.target.value.slice(0, MAX_LEN))}
                    onBlur={() => setTouched(true)}
                    placeholder={t.reasonPlaceholder}
                    maxLength={MAX_LEN}
                    resize="vertical"
                  />

                  <Field.HelperText>{t.counter(reason.length, MAX_LEN)}</Field.HelperText>

                  {reasonError ? <Field.ErrorText>{reasonError}</Field.ErrorText> : null}
                </Field.Root>
              </Box>

              {error ? (
                <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Description>{error}</Alert.Description>
                </Alert.Root>
              ) : null}
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={onClose} disabled={!!loading}>
                {t.cancel}
              </Button>

              <Button
                colorPalette="red"
                loading={!!loading}
                disabled={!canSubmit}
                onClick={() => onConfirm(trimmed)}
              >
                {t.confirm}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
